// Decide whether a persisted cart should be DISCARDED (so a fresh one is created) or KEPT,
// given the outcome of re-validating it against the server on load.
//
// Why this exists: the customer app persists the cart to localStorage and, on return, trusts
// it blindly. A cart that has been deleted, is no longer owned by this browser session (the
// server identity rotated), or is already purchased will 404 on every request and wedge the
// page until the customer clears cookies (the "clear cookies fixes it" class — #4641). We fix
// that by validating the cart first and self-healing.
//
// The rules are deliberately conservative so we NEVER throw away a live, in-progress cart
// (that would lose a parent's selections):
//   - HTTP 404 / 403  -> discard  (cart is gone, or not owned by this session)
//   - any other error -> KEEP     (transient: a network blip or 5xx must not nuke a good cart)
//   - server says no such cart (found === false) -> discard
//   - found & purchased -> discard (already checked out; start a fresh order)
//   - found & not purchased -> KEEP (a valid, in-progress cart — protect the customer's work)
//
// `outcome`:
//   - found:      true | false | null  (null = we didn't get a definitive answer, e.g. an error)
//   - purchased:  boolean
//   - httpStatus: number | null        (the response status, or null on success / no response)
export function shouldReplaceCart({ found = null, purchased = false, httpStatus = null } = {}) {
  if (httpStatus === 404 || httpStatus === 403) return true; // gone / not owned
  if (httpStatus != null && httpStatus >= 400) return false; // transient -> keep
  if (found === false) return true; // server has no such cart
  if (found && purchased) return true; // already purchased -> fresh order
  return false; // valid, in-progress cart -> keep
}

// The ONE place that knows how to read an HTTP status off a spraypaint error.
// Spraypaint rejects with ResponseError carrying the raw fetch Response
// (`.status`), or null on a network failure — there is no `.statusCode`
// variant. Both CustomerContext catches share this so the shape logic can't
// drift between copies.
export function httpStatusFromError(err) {
  return err?.response?.status ?? null;
}

export default shouldReplaceCart;
